import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  SimpleTable,
  SimpleTableBody,
  SimpleTableContainer,
  SimpleTableData,
  SimpleTableHead,
  SimpleTableHeader,
  SimpleTableRow,
  SimpleTableWrapper,
  Checkbox,
  CHECKBOX_SIZES,
  INPUT_SIZES,
  SearchInput,
  IconInfo,
  withTooltip,
  TOOLTIP_HORIZONTAL_ALIGNMENTS,
  TOOLTIP_VERTICAL_ALIGNMENTS,
  THEMES,
  Button,
  StepContent,
  Dialog,
  BUTTON_VARIANTS,
  Content,
  Toast,
  TOAST_VARIANTS,
  TOAST_VISIBILITY_DURATIONS
} from 'cdk-radial';
import styled from 'styled-components';
import CMFSelectorDropdown from './CMFSelectorDropdown';
import { ACTIONS } from '../../utils/daabSubscriptions';
import { CMF_STATUS, PAGE_TITLE } from '../../../../constants/constants';
import './DAABSubscriptions.scss';
import { NoDataOverlay } from '../../utils/NoDataOverlay';
import Page from '../../../page';
import BackButton from '../../BackButton';
import { getPlanName, PLAN_TYPE } from '../../utils/apiPricingUtil';
import { setScreen } from '../../../../redux/connectedComponent/uiFactory/uiFactory.slice';
import { addBundleLocations, clearAddBundleLocationsState, removeBundleLocations, clearRemoveBundleLocationsState } from '../../../../redux/connectedComponent/orderAPI/orderAPI.slice';
import { trackConfirmEstimatedMonthlyBill, trackPageView, PAGE_SECTIONS, PAGE_SUBSECTIONS } from '../../../../utils/gtm';
import {
  getOrgAmplitudeProperties,
  sendAmplitudeData,
} from '../../../../utils/amplitude';
import { AMPLITUDE_EVENTS } from '../../../../constants/amplitudeConstants';

const ADD_BUNDLE_LOCATION_SUB_HEADING = 'Select the dealership locations where you would like to use this Bundle.';
const BUNDLE_SAVE_DIALOG_CONTENT = {
  TITLE: 'Estimated Monthly Bill',
  SUB_HEADING: 'This is calculated from the number of active locations and the cost per location in your bundle reflected in your next billing cycle.',
}
const StyledSimpleTableRow = styled(SimpleTableRow)`
  height: 32px !important; // Force height for the header row
`;

const StyledSimpleTableHeader = styled(SimpleTableHeader)`
  position: sticky;
  top: 0;
  height: 32px !important;
  vertical-align: middle;
`;

const StickySimpleTable = styled(SimpleTable)`
  position: relative;
  overflow: auto;
`;

const StickySimpleTableContainer = styled(SimpleTableContainer)`
  height: 400px;
  overflow: auto;
`;

const ZebraSimpleTableRow = styled(SimpleTableRow)`
  :nth-child(even) {
    background-color: ${({ theme }) =>
      theme.color.zebraStripe.background.value};
    border-top: 1px solid ${({ theme }) => theme.color.gray[200].value};
    :hover {
      background-color: ${({ theme }) => theme.color.zebraStripe.hover.value};
    }
  }
  &:last-child {
    border-bottom: none;
  }
`;

const StyledIconInfo = styled(IconInfo)`
  fill: #151b25b2;
  padding: 4px;
`;

const RemoveLocationsToast = () => {
  const CONTENT =
    'Removed locations will be unsubscribed from all APIs in the bundle.';
  return (
    <Toast
      content={CONTENT}
      id="remove-bundle-locations-toast"
      visibilityDuration={TOAST_VISIBILITY_DURATIONS.IconInfo}
      variant={TOAST_VARIANTS.WARNING}
      style={{ marginBottom: '16px' }}
    />
  );
};

export function processLocations(inputData = [], isRemoveLocations) {
  const filteredData = !isRemoveLocations
    ? inputData.filter((e) => e?.status !== CMF_STATUS.INACTIVE)
    : inputData;
  const groupedStores = {};
  for (const store of filteredData) {
    const entityId = store?.entityId;
    if (!entityId) continue;
    const cmfValues = Array.isArray(store.cmfNumber)
      ? store.cmfNumber.map((c) => c && c.toString())
      : [store.cmfNumber && store.cmfNumber.toString()];

    if (!groupedStores[entityId]) {
      groupedStores[entityId] = { ...store, cmfNumber: [] };
    }
    cmfValues.forEach((val) => {
      if (val && !groupedStores[entityId].cmfNumber.includes(val)) {
        groupedStores[entityId].cmfNumber.push(val);
      }
    });
  }
  return Object.values(groupedStores);
}

const StyledDialog = styled(Dialog)`
    max-width: 700px;
  `;
export const BundleLocationsTable = ({
  handleLocationSelection,
  isRemoveLocations,
  id,
  name,
  type,
  orgId,
  webhookUrl,
  planPrice = 0,
  planDetails,
  orderId,
  bundleLocations,
  apis = [],
}) => {
  const dispatch = useDispatch();
  const state = useSelector((state) => state);
  const { prevScreen } = state?.uiFactorySlice;
  const { authenticationToken: token } = state?.loginDetailsSlice;
  const orgAmplitudeProperties = getOrgAmplitudeProperties(
    state?.loginDetailsSlice
  );
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const allLocations = processLocations(
    state?.loginDetailsSlice?.allLocationsWithoutMergingCmfs || [],
    isRemoveLocations
  );
  const [value, setValue] = useState('');

  const [selectedRows, setSelectedRows] = useState(new Set());
  const [selectedCmf, setSelectedCmf] = useState({});
  const [originallySelectedRows, setOriginallySelectedRows] = useState(new Set());

  const isBundleSubscription =
      planDetails?.plan_type === PLAN_TYPE.BUNDLE_SUBSCRIPTION.toLowerCase();
  const planTitle = getPlanName(
    isBundleSubscription ? PLAN_TYPE.SUBSCRIPTION : planDetails?.plan_type,
    true,
    planDetails?.name,
  );

  useEffect(() => {
    trackPageView({
      page_section: PAGE_SECTIONS.MY_APIS,
      page_subsection: PAGE_SUBSECTIONS.MANAGE_BUNDLE_LOCATIONS,
    });
  }, []);

  const onBack = () => {
    dispatch(
      setScreen({
        screen: prevScreen,
        additionalData: { id, type, webhookUrl, name, orgId },
      })
    );
  };


  useEffect(() => {
    if (!bundleLocations || bundleLocations.length === 0) return;

    const newSelectedRows = new Set();
    const newOriginallySelectedRows = new Set();
    const newSelectedCmf = {};

    bundleLocations.forEach((loc) => {
      if (loc.bundleId === id) {
        newSelectedRows.add(loc.orgId);
        newOriginallySelectedRows.add(loc.orgId);
        newSelectedCmf[loc.orgId] = loc.cmf;
      }
    });

    setSelectedRows(newSelectedRows);
    setOriginallySelectedRows(newOriginallySelectedRows);
    setSelectedCmf(newSelectedCmf);
  }, [bundleLocations, id]);

  useEffect(() => {
    const tableData =
      Array.isArray(allLocations) &&
      allLocations
        .map(location => ({
          id: location.entityId,
          location: location?.partyName,
          address:
            location?.addresses[0] &&
            location?.addresses[0].streetAddress +
              ', ' +
              location?.addresses[0].city,
          isRowSelected: selectedRows.has(location.entityId),
          cmf: location.cmfNumber.map((cmf) => ({ label: cmf, value: cmf })),
          storeId: location.storeId,
        }));
    setData(tableData);
    setFilteredData(tableData);
  }, [state?.loginDetailsSlice, selectedRows]);

  useEffect(() => {
    if (!value) {
      setFilteredData(data);
    } else {
      const lowercasedValue = value.toLowerCase();
      const filtered = data.filter(
        (row) =>
          row.location.toLowerCase().includes(lowercasedValue) ||
          row.address.toLowerCase().includes(lowercasedValue) ||
          row.cmf?.some((item) => item?.value?.includes(value))
      );
      setFilteredData(filtered);
    }
  }, [value, data]);

  const handleRowClicked = async (selectedRow) => {
    const { id, address, location, cmf, storeId } = selectedRow;
    const cmfNumber = cmf.length === 1 ? cmf[0].value : selectedCmf[id];
    setSelectedRows((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
        if (!originallySelectedRows.has(id)) {
          setSelectedCmf((prev) => {
            const updated = { ...prev };
            delete updated[id];
            return updated;
          });
        }
        if (handleLocationSelection) {
          handleLocationSelection({
            locationData: { entityId: id, address, location },
            action: ACTIONS.REMOVE_LOCATION,
          });
        }
      } else {
        newSet.add(id);
        if (handleLocationSelection) {
          handleLocationSelection({
            locationData: {
              entityId: id,
              cmfNumber,
              storeId,
              address,
              location,
            },
            action: ACTIONS.ADD_LOCATION,
          });
        }
      }
      return newSet;
    });
  };

  const getNetChanges = () => {
    const added = Array.from(selectedRows).filter(
      (rowId) => !originallySelectedRows.has(rowId)
    );
    const removed = Array.from(originallySelectedRows).filter(
      (rowId) => !selectedRows.has(rowId)
    );
    return { added, removed };
  };

  const handleCmfSelect = (rowId, option, storeId) => {
    setSelectedCmf((prev) => ({
      ...prev,
      [rowId]: option?.value,
    }));
    handleLocationSelection({
      locationData: { entityId: rowId, storeId },
      action: ACTIONS.UPDATE_CMF,
      cmf: option?.value,
    });
  };
  const isSaveButtonDisabled = () => {
    const { added, removed } = getNetChanges();

    if (added.length === 0 && removed.length === 0) {
      return true;
    }

    const addedData = data.filter((row) => added.includes(row.id));
    for (const row of addedData) {
      if (
        Array.isArray(row.cmf) &&
        row.cmf.length > 1 &&
        !selectedCmf[row.id]
      ) {
        return true;
      }
    }
    return false;
  };

  const handleSave = () => {
    setOpenDialog(true);
  };

  const handleConfirmSave = async () => {
    try {
      const { added, removed } = getNetChanges();
      trackConfirmEstimatedMonthlyBill(id, name, added.length);
      setIsSaving(true);

      if (added.length > 0) {
        sendAmplitudeData(
          AMPLITUDE_EVENTS.MANAGE_LOCATIONS_ADD_LOCATIONS,
          orgAmplitudeProperties
        );
      }
      if (removed.length > 0) {
        sendAmplitudeData(
          AMPLITUDE_EVENTS.MANAGE_LOCATIONS_REMOVE_LOCATIONS,
          orgAmplitudeProperties
        );
      }

      const promises = [];

      if (added.length > 0) {
        const addedLocationsData = added.map((entityId) => {
          const locationData = data.find((loc) => loc.id === entityId);
          return {
            entityId,
            cmfNumber:
              selectedCmf[entityId] ||
              (locationData?.cmf?.length === 1
                ? locationData.cmf[0].value
                : null),
          };
        });
        promises.push(
          dispatch(
            addBundleLocations({
              locations: addedLocationsData,
              token,
              bundleId: id,
              orderId,
              bundleDisplayName: name,
            })
          )
        );
      }

      if (removed.length > 0) {
        promises.push(
          dispatch(
            removeBundleLocations({
              orgIds: removed,
              bundleId: id,
              apis,
              token,
              orderId,
              bundleDisplayName: name,
            })
          )
        );
      }

      await Promise.allSettled(promises);
      setOpenDialog(false);
      dispatch(setScreen({ screen: prevScreen, additionalData: { bundleName: name } }));
    } catch (error) {
      console.error('Error saving bundle locations:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const calculateEstimatedCost = () => {
    const numberOfSelectedLocations = selectedRows.size;
    const costPerLocation = planPrice || 0;
    return (numberOfSelectedLocations * costPerLocation).toFixed(2);
  };

  return (
    <>
      <Page pageHeader={PAGE_TITLE.MY_APIS}>
        <BackButton onBack={onBack} />
        <div className="main-menu-container">
          <div className="add-locations-page">
            <section className="add-locations-page-header-container">
              <div className="heading">Manage Locations for {name}</div>
            </section>
            <section>
              <div className="manage-locations-grid">
                <div className="subheader-search-wrapper">
                  <div className="add-locations-subheader">
                    {ADD_BUNDLE_LOCATION_SUB_HEADING}
                  </div>
                  <SearchInput
                    className="manage-location-search-input"
                    clearButtonLabel="clearButtonLabel"
                    data-testId="location-search-input"
                    id="location-search-input"
                    isAlwaysExpanded
                    name="location-search-input"
                    onChange={(input) => setValue(input)}
                    placeholder="Search locations by name or CMF number"
                    size={INPUT_SIZES.STANDARD}
                    value={value}
                  />
                </div>
                <SimpleTableWrapper data-testid="simple-table-wrapper">
                  <StickySimpleTableContainer className="manage-locations-table-container">
                    <StickySimpleTable aria-label="Table">
                      <SimpleTableHead>
                        <StyledSimpleTableRow className="manage-locations-table-header">
                          <StyledSimpleTableHeader
                            style={{ width: '1%' }}
                            className="manage-locations-table-toggle-column"
                          ></StyledSimpleTableHeader>
                          <StyledSimpleTableHeader
                            style={{ width: '28%' }}
                          >{`Dealership Location (${data?.length})`}</StyledSimpleTableHeader>
                          <StyledSimpleTableHeader style={{ width: '30%' }}>
                            Address
                          </StyledSimpleTableHeader>
                          <StyledSimpleTableHeader style={{ width: '20%' }}>
                            CMF Number
                          </StyledSimpleTableHeader>
                        </StyledSimpleTableRow>
                      </SimpleTableHead>
                      <SimpleTableBody className="manage-locations-table-body">
                        {filteredData.length > 0 ? (
                          filteredData.map((row) => (
                            <ZebraSimpleTableRow
                              key={row.id}
                              isSelected={row.isSelected}
                              className="manage-locations-table-rows"
                            >
                              <SimpleTableData style={{ width: '1%' }}>
                                <Checkbox
                                  name={`location-checkbox-${row.id}`}
                                  data-testid={`location-checkbox-${row.id}`}
                                  checked={row.isRowSelected}
                                  onChange={() => handleRowClicked(row)}
                                  size={CHECKBOX_SIZES.STANDARD}
                                />
                              </SimpleTableData>
                              <SimpleTableData
                                style={{
                                  width: '30%',
                                  fontWeight: '500',
                                  fontSize: '13px',
                                  lineHeight: '20px',
                                }}
                              >
                                {row?.location}
                              </SimpleTableData>
                              <SimpleTableData
                                style={{
                                  width: '30%',
                                  fontSize: '13px',
                                  lineHeight: '20px',
                                  color: '#151B25',
                                }}
                              >
                                {row?.address}
                              </SimpleTableData>
                              <SimpleTableData
                                style={{
                                  width: '18%',
                                  fontSize: '13px',
                                  lineHeight: '20px',
                                  color: '#151B25',
                                }}
                              >
                                {Array.isArray(row.cmf) &&
                                row.cmf.length === 1 ? (
                                  row.cmf[0].value
                                ) : (
                                  <div className="cmf-dropdown-tooltip-wrapper">
                                    <CMFSelectorDropdown
                                      inputId={row.id}
                                      options={row.cmf}
                                      storeId={row.storeId}
                                      isDisabled={!row.isRowSelected || originallySelectedRows.has(row.id)}
                                      value={selectedCmf[row.id]}
                                      handleCmfSelect={handleCmfSelect}
                                    />
                                    {!isRemoveLocations && <CMFToolTip />}
                                  </div>
                                )}
                              </SimpleTableData>
                            </ZebraSimpleTableRow>
                          ))
                        ) : (
                          <StyledSimpleTableRow>
                            <SimpleTableData
                              colSpan={4}
                              style={{
                                textAlign: 'center',
                                color: '#151B25B3',
                                backgroundColor: '#FFFFFF',
                              }}
                            >
                              <NoDataOverlay
                                message={`No locations to show.`}
                              />
                            </SimpleTableData>
                          </StyledSimpleTableRow>
                        )}
                      </SimpleTableBody>
                    </StickySimpleTable>
                  </StickySimpleTableContainer>
                </SimpleTableWrapper>
              </div>
            </section>
            <StepContent className="daab-add-location-stepper-footer">
              <Button
                dataTestId="save-bundle-locations-button"
                iconColor="primary"
                iconSize={24}
                onClick={handleSave}
                size="standard"
                text="Continue"
                variant="primary"
                isDisabled={isSaveButtonDisabled()}
              />
            </StepContent>
          </div>
        </div>
      </Page>
      {openDialog ? (
        <StyledDialog
          className="bundle-manage-locations-confirm-dialog"
          buttonsProps={[
            {
              id: 'cancel-action',
              onClick: () => setOpenDialog(false),
              text: 'Cancel',
              variant: BUTTON_VARIANTS.SECONDARY,
              isDisabled: isSaving,
            },
            {
              id: 'confirm-action',
              onClick: handleConfirmSave,
              text: 'Save',
              variant: BUTTON_VARIANTS.PRIMARY,
              isLoading: isSaving,
            },
          ]}
          id="bundle-locations-confirm-dialog"
          dataTestId="bundle-locations-confirm-dialog"
          isOpen={openDialog}
          onClose={() => !isSaving && setOpenDialog(false)}
          title={BUNDLE_SAVE_DIALOG_CONTENT.TITLE}
          disableOverlayClicked={true}
        >
          <Content className="bundle-manage-locations-dialog-content">
            {getNetChanges().removed.length > 0 && <RemoveLocationsToast />}
            <div>{BUNDLE_SAVE_DIALOG_CONTENT.SUB_HEADING}</div>
            <section className="estimated-bill-summary">
              <div className="heading">Summary</div>
              <div className="main-text">{selectedRows.size} Locations</div>
              <section className="price-section">
                <div className="body-text">{planTitle}</div>
                <div className="body-text">${(planPrice || 0).toFixed(2)}<sup>*</sup></div>
              </section>
              <div className="divider"></div>
              <section className="estimated-cost-section">
                <div>
                  <div className="main-text">Estimated Monthly Bill</div>
                  <div className="sub-text">*Does not include usage fees or taxes.</div>
                </div>
                <div className="main-text">${calculateEstimatedCost()}</div>
              </section>
            </section>
          </Content>
        </StyledDialog>
      ) : null}
    </>
  );
};

BundleLocationsTable.defaultProps = {
  handleLocationSelection: () => {},
  isRemoveLocations: false,
  id: '',
  name: '',
  type: '',
  orgId: '',
  webhookUrl: '',
  planPrice: 0,
  activePricingPlanId: '',
  planDetails: null,
  orderId: '',
  bundleLocations: null,
};

const CMFToolTip = () => {
  const tooltipProps = {
    id: 'cmf-tooltip',
    text: "If you are not sure of the correct CMF #, speak with your Dealer's Enterprise Administrator or contact CDK Customer Care at 866.668.5394, option 8",
    theme: THEMES.LIGHT,
    styles: { zIndex: 1001 },
  };
  const ComponentToRender = ({ ...props }) => {
    return <StyledIconInfo {...props} />;
  };
  const ComponentWithTooltip = withTooltip(ComponentToRender, tooltipProps);

  return (
    <ComponentWithTooltip
      horizontalAlignment={TOOLTIP_HORIZONTAL_ALIGNMENTS.LEFT}
      marginAroundElement={8}
      verticalAlignment={TOOLTIP_VERTICAL_ALIGNMENTS.TOP}
    />
  );
};
